/**
 * Address suggestions via Photon (OpenStreetMap) — no API key.
 * Fills street / city / zip / country select when the user picks a result.
 */
window.WinwinAddressAutofill = (function () {
  var ENDPOINT = 'https://photon.komoot.io/api/';
  var DEBOUNCE_MS = 280;

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function streetLine(props) {
    if (props.street) {
      return [props.street, props.housenumber].filter(Boolean).join(' ');
    }
    return props.name || '';
  }

  function labelFor(props) {
    var parts = [
      streetLine(props) || props.name,
      props.postcode,
      props.city || props.town || props.village || props.locality,
      props.state,
      props.country
    ].filter(Boolean);
    // Deduplicate consecutive identical parts
    var out = [];
    parts.forEach(function (p) {
      if (!out.length || out[out.length - 1] !== p) out.push(p);
    });
    return out.join(', ');
  }

  function streetFrom(props) {
    var line = streetLine(props);
    if (line) return line;
    return props.name || '';
  }

  function cityFrom(props) {
    return props.city || props.town || props.village || props.locality || props.county || '';
  }

  function matchCountry(select, countryName) {
    if (!select || !countryName) return;
    var needle = String(countryName).trim().toLowerCase();
    var options = Array.prototype.slice.call(select.options || []);
    var hit = options.find(function (o) {
      return (o.textContent || '').trim().toLowerCase() === needle;
    });
    if (!hit) {
      hit = options.find(function (o) {
        var t = (o.textContent || '').trim().toLowerCase();
        return t.indexOf(needle) === 0 || needle.indexOf(t) === 0;
      });
    }
    if (hit && hit.value) {
      select.value = hit.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function attach(streetInput, form) {
    if (!streetInput || !form || streetInput.dataset.autofillBound === '1') return;
    streetInput.dataset.autofillBound = '1';
    streetInput.setAttribute('autocomplete', streetInput.getAttribute('autocomplete') || 'street-address');
    streetInput.setAttribute('role', 'combobox');
    streetInput.setAttribute('aria-autocomplete', 'list');
    streetInput.setAttribute('aria-expanded', 'false');

    var wrap = document.createElement('div');
    wrap.className = 'addr-suggest';
    streetInput.parentNode.insertBefore(wrap, streetInput);
    wrap.appendChild(streetInput);

    var list = document.createElement('ul');
    list.className = 'addr-suggest__list';
    list.hidden = true;
    list.setAttribute('role', 'listbox');
    wrap.appendChild(list);

    var timer = null;
    var items = [];
    var active = -1;
    var suppressSearch = false;

    function close() {
      window.clearTimeout(timer);
      list.hidden = true;
      list.innerHTML = '';
      items = [];
      active = -1;
      streetInput.setAttribute('aria-expanded', 'false');
    }

    function render() {
      if (!items.length) {
        close();
        return;
      }
      list.innerHTML = items.map(function (item, i) {
        return '<li class="addr-suggest__item' + (i === active ? ' is-active' : '') +
          '" role="option" data-idx="' + i + '">' + escapeHtml(item.label) + '</li>';
      }).join('');
      list.hidden = false;
      streetInput.setAttribute('aria-expanded', 'true');
    }

    function apply(item) {
      if (!item) return;
      suppressSearch = true;
      window.clearTimeout(timer);
      streetInput.value = item.street || item.label;
      var city = form.querySelector('[name="city"]');
      var zip = form.querySelector('[name="zip"]');
      var state = form.querySelector('[name="state_region"]');
      var country = form.querySelector('[name="countries_idcountry"]');
      if (city && item.city) city.value = item.city;
      if (zip && item.zip) zip.value = item.zip;
      if (state && item.state) state.value = item.state;
      matchCountry(country, item.country);
      close();
      // Do not fire `input` — that re-opens suggestions. `change` is enough for listeners.
      streetInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (city) city.dispatchEvent(new Event('change', { bubbles: true }));
      if (zip) zip.dispatchEvent(new Event('change', { bubbles: true }));
      window.setTimeout(function () {
        suppressSearch = false;
      }, 400);
    }

    async function search(q) {
      if (suppressSearch) return;
      if (q.length < 3) {
        close();
        return;
      }
      try {
        var url = ENDPOINT + '?q=' + encodeURIComponent(q) + '&limit=6&lang=en';
        var res = await fetch(url);
        if (!res.ok) throw new Error('photon ' + res.status);
        var data = await res.json();
        if (suppressSearch) return;
        items = (data.features || []).map(function (f) {
          var p = f.properties || {};
          return {
            label: labelFor(p),
            street: streetFrom(p),
            city: cityFrom(p),
            zip: p.postcode || '',
            state: p.state || '',
            country: p.country || ''
          };
        }).filter(function (x) { return x.label; });
        active = -1;
        render();
      } catch (err) {
        close();
      }
    }

    streetInput.addEventListener('input', function () {
      if (suppressSearch) return;
      window.clearTimeout(timer);
      var q = streetInput.value.trim();
      timer = window.setTimeout(function () { search(q); }, DEBOUNCE_MS);
    });

    streetInput.addEventListener('keydown', function (e) {
      if (list.hidden || !items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        active = (active + 1) % items.length;
        render();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        active = (active - 1 + items.length) % items.length;
        render();
      } else if (e.key === 'Enter' && active >= 0) {
        e.preventDefault();
        apply(items[active]);
      } else if (e.key === 'Escape') {
        close();
      }
    });

    list.addEventListener('mousedown', function (e) {
      var li = e.target.closest('[data-idx]');
      if (!li) return;
      e.preventDefault();
      e.stopPropagation();
      apply(items[Number(li.getAttribute('data-idx'))]);
    });

    list.addEventListener('click', function (e) {
      var li = e.target.closest('[data-idx]');
      if (!li) return;
      e.preventDefault();
      e.stopPropagation();
      apply(items[Number(li.getAttribute('data-idx'))]);
    });

    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) close();
    });
  }

  function bindForm(form) {
    if (!form) return;
    var street = form.querySelector('[name="street1"]');
    if (street) attach(street, form);
  }

  return { bindForm: bindForm, attach: attach };
})();
